<script setup lang="ts" generic="Value">
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import { getSelectionValue, getSelectPassdownProps } from '@/components/forms/Select/utils';
import { computed, useAttrs } from 'vue';
import { VAutocomplete } from 'vuetify/components';
import FormSelectionDisplay from '../display/FormSelectionDisplay.vue';
import { AutocompleteProps } from '../types';
import { getItemsForSelection, getPrefixedUuid } from '../utils';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<AutocompleteProps<Value>>(), {
  mode: 'edit',
  sortItems: false,
});

const passdownProps = computed(() => ({
  ...props,
  label: undefined,
  ...getSelectPassdownProps(props),
}));

// Generate internal key for VAutocomplete to handle dependent dropdown scenarios.
// When parent selections change in cascading dropdowns (e.g., category -> subCategory -> manufacturer),
// the items array updates but Vuetify's internal state can retain stale values. This computed key
// forces the component to remount when items change, ensuring the field clears properly without
// requiring parent components to manage :key attributes explicitly.
const internalKey = computed(() => {
  const itemsHash = passdownProps.value.items
    ?.map((item) => {
      if (!item) return '';
      return typeof item === 'object' && 'value' in item ? String(item.value) : String(item);
    })
    .join(',');
  return itemsHash || 'empty';
});

const id = getPrefixedUuid('autocomplete');

const attrs = useAttrs();
const passdownAttrs = computed(() => ({
  ...attrs,
  class: undefined,
}));

const emits = defineEmits(['update:modelValue']);

function onUpdate(value: any) {
  emits('update:modelValue', getSelectionValue(value, props));
}

const selectedItems = computed(() => {
  const itemsForSelection = getItemsForSelection(
    passdownProps.value.items,
    props.modelValue,
    props.valueComparator,
  );
  return props.sortDisplayItems
    ? itemsForSelection?.sort((left, right) => left.title.localeCompare(right.title))
    : itemsForSelection;
});
</script>

<template>
  <div class="autocomplete" :class="$attrs.class">
    <FormLabel :for="id" :infoMessage="infoMessage" :label="label" :disabled="$props.disabled">
      <template #default="{ onBlur, onFocus, hoverProps }">
        <v-autocomplete
          v-if="mode === 'edit' || mode === 'add'"
          :key="internalKey"
          v-bind="{ ...passdownProps, ...passdownAttrs, ...hoverProps, id } as any"
          :menuProps="{ contentClass: 'autocompleteOverlay' }"
          :error="Boolean(errorMessage)"
          :label="label"
          :hint="errorMessage || hint"
          :persistentHint="Boolean(errorMessage) || persistentHint"
          :no-data-text="$t('NoData')"
          @update:modelValue="onUpdate"
          @focus="onFocus()"
          @blur="onBlur()"
        >
          <template v-if="$slots.item" #item="slotData">
            <slot name="item" v-bind="{ ...slotData, item: slotData.internalItem }" />
          </template>
          <template v-else-if="multiple" #item="slotData">
            <v-list-item v-bind="slotData.props">
              <template #prepend="{ isSelected }">
                <v-list-item-action start>
                  <v-checkbox-btn :modelValue="isSelected"></v-checkbox-btn>
                </v-list-item-action>
              </template>
              <template #title>
                <slot v-if="$slots.itemTitle" name="itemTitle" v-bind="slotData.internalItem" />
                <span v-else>{{ slotData.internalItem.title }}</span>
              </template>
            </v-list-item>
          </template>
          <template v-if="$slots.chip" #chip="slotData">
            <slot name="chip" v-bind="{ ...slotData, item: slotData.internalItem }" />
          </template>
          <template v-else-if="chips" #chip="slotData">
            <v-chip :closable="true" class="text-body-2" v-bind="slotData.props">
              <slot v-if="$slots.itemTitle" name="itemTitle" v-bind="slotData.internalItem" />
              <span v-else>{{ slotData.internalItem.title }}</span>
            </v-chip>
          </template>
          <template v-if="$slots.appendItem" #append-item>
            <slot name="appendItem" />
          </template>
          <template v-if="$slots.append" #append>
            <slot name="append" />
          </template>
        </v-autocomplete>

        <FormSelectionDisplay v-else :value="selectedItems" :plainText="$props.plainText">
          <template v-if="$slots.itemTitle" #itemTitle="slotData">
            <slot name="itemTitle" v-bind="slotData" />
          </template>
        </FormSelectionDisplay>
      </template>
    </FormLabel>
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/mixins';

.autocomplete {
  width: 100%;
}

:deep(.v-autocomplete) {
  width: 100%;

  input {
    opacity: 1 !important;
  }

  // Vuetify sets placeholder opacity to 0 until the field is active, because its label
  // occupies that space. FormLabel replaces that label, so an empty field keeps the placeholder.
  .v-field:not(.v-field--active) input::placeholder {
    opacity: var(--v-disabled-opacity);
  }
}

:deep(.v-label) {
  display: none;
}

:deep(.v-list-item-title) {
  @include mixins.typography('body-2');
}
</style>
